import { EBM_RESOURCE_CONFIG_OPTION_META_KEYS } from '../../capabilities/ebm-nodes/fieldKeys';
import { buildDiskPricingWatchFields, buildVendorDeviceMetaWatchFields } from '../../components/vendorDevice';
import { resolvePricingResourceCodes, resolveWatchFieldsFromResourceConfigs } from '../../platform/pipeline-kernel/resolveResourceWatch';
import type { ResourceConfigEntry, ResourceType, SkuModuleMeta } from '../../platform/pipeline-kernel/types';

/** 与 buildEbmResourceConfigs / skuResources 投影 code 一致 */
/** 计价复合资源：SKU / 下单 ElasticBareMetalNode 厂商设备 */
export const ebmElasticBareMetalNode: ResourceType = {
  requirement: '1',
  code: 'ElasticBareMetalNode',
  description: '弹性裸金属节点',
  unit: '台',
};

export const ebmCpu: ResourceType = {
  requirement: '',
  code: 'CPU',
  description: 'CPU',
  unit: 'Core',
};

export const ebmMemory: ResourceType = {
  requirement: '',
  code: 'Memory',
  description: 'Memory',
  unit: 'Gi',
};

export const ebmGpu: ResourceType = {
  requirement: '',
  code: 'GPU',
  description: 'GPU',
  unit: 'Card',
};

export const ebmNic: ResourceType = {
  requirement: '',
  code: 'NIC',
  description: '网卡',
  unit: 'Card',
};

export const ebmNvme: ResourceType = {
  requirement: '',
  code: 'NVMe',
  description: '存储',
  unit: 'Card',
};

export const ebmSystemStorage: ResourceType = {
  requirement: '',
  code: 'SystemDisk',
  description: '系统盘',
  unit: 'Gi',
};

export const ebmDataStorage: ResourceType = {
  requirement: '',
  code: 'DataDisk',
  description: '数据盘',
  unit: 'Gi',
};

export const ebmComputeTemplates = {
  ebmElasticBareMetalNode,
  ebmCpu,
  ebmMemory,
  ebmGpu,
  ebmNic,
  ebmNvme,
  ebmSystemStorage,
  ebmDataStorage,
};

/**
 * 弹性裸金属 innerForm 资源条目：watchFields 与 headless/projections.buildEbmResourceConfigs 同源。
 * SKU / 下单通过 platform.useResourceFieldChange 监听这些字段触发计价。
 */
export const EBM_RESOURCE_CONFIGS: ResourceConfigEntry[] = [
  {
    ...ebmElasticBareMetalNode,
    watchFields: [
      'resourceConfiguration',
      'optionMeta',
      ...buildVendorDeviceMetaWatchFields(`optionMeta.${EBM_RESOURCE_CONFIG_OPTION_META_KEYS.ebmNode}`),
    ],
    displayIds: ['ebmNode'],
  },
  {
    ...ebmCpu,
    watchFields: ['resourceConfiguration', 'resource.cpu'],
    displayIds: ['cpu'],
  },
  {
    ...ebmMemory,
    watchFields: ['resourceConfiguration', 'resource.memory'],
    displayIds: ['memory'],
  },
  {
    ...ebmGpu,
    watchFields: ['resourceConfiguration', 'resource.gpu'],
    displayIds: ['gpu'],
  },
  {
    ...ebmNic,
    watchFields: ['resourceConfiguration'],
    displayIdPrefix: 'nic',
  },
  {
    ...ebmNvme,
    watchFields: ['resourceConfiguration'],
    displayIdPrefix: 'nvme',
  },
  {
    ...ebmSystemStorage,
    watchFields: ['systemDisk', 'systemDisk.diskSize', ...buildDiskPricingWatchFields('systemDisk')],
    displayIds: ['systemDisk'],
  },
];

/**
 * 数据盘 ResourceType 模板仍供 projections 使用。
 * 无 dataDisk 模块/UI，不纳入 EBM_RESOURCE_CONFIGS 计价 watch；上线 UI 后再加回 watchFields。
 */
/** SKU 模块：是否参与 resourceConfigs 投影 / 预置校验 */
export const EBM_SKU_MODULE_META: Record<string, SkuModuleMeta> = {
  baseInfo: {
    resource: false,
    presetCheckFields: ['name'],
  },
  network: {
    resource: false,
    presetCheckFields: ['resource.network.vpc.name'],
  },
  systemSetting: {
    resource: true,
    presetCheckFields: ['runStrategy', 'systemDisk'],
    /** 系统盘容量；仅 diskSize 有预填则锁 */
    quantityFields: ['disk'],
    quantityPresetPaths: { disk: ['systemDisk.diskSize'] },
  },
  resource: {
    resource: true,
    presetCheckFields: ['resourceConfiguration'],
  },
  service: {
    resource: false,
    presetCheckFields: ['resource.network.service'],
    orderListItemLock: true,
  },
  dns: {
    resource: false,
    orderListItemLock: true,
    presetCheckFields: [
      'resource.network.dnsConfig.nameservers',
      'resource.network.dnsConfig.searches',
      'resource.network.dnsConfig.options',
    ],
  },
};

export function resolveEbmResourceWatchFields(configs: ResourceConfigEntry[] = EBM_RESOURCE_CONFIGS): string[] {
  return resolveWatchFieldsFromResourceConfigs(configs);
}

export function resolveEbmPricingResourceCodes(configs: ResourceConfigEntry[] = EBM_RESOURCE_CONFIGS): Set<string> {
  return resolvePricingResourceCodes(configs);
}
